import { getContext, setContext } from 'svelte';
import { derived, get, type Readable, readable, writable } from 'svelte/store';
import {
  type CreateMutateFunction,
  createMutation,
  createQuery,
} from '$lib/api/tanstack-query';

import { useNotify } from '@viamrobotics/prime-core';
import * as APITypes from '@viamrobotics/web-client/app/v1/app';

import { assertExists } from '$lib/assert';
import { AuthStatus, useAuthContext } from '$lib/auth';
import { useStoreValue } from '$lib/store';
import type { QueryStateOptions } from '$lib/types/query';

import { useAPIClient } from '../client';
import { useQueryInvalidator } from '../invalidate';
import { locationKeys } from '../locations/query-key';
import { useAuthsByIdentityIDMap } from '../permissions';
import { createQueryOptions } from '../query-options';
import { useCreateSetUserOrgLastAccessMutation, useUserContext } from '../user';
import {
  buildOrganizationMembers,
  sortInvitesByRoleThenEmail,
} from './build-members-and-invites';
import { setBrowsersSessionOrgID } from './default-org-id';
import { findOrgMemberByEmail } from './members';
import {
  getCurrentOrg,
  getCurrentOrgID,
  type Organizations,
} from './get-current-org';
import { createListOrganizationsQuery } from './list-organizations';
import { getOrgQueryKeys, orgKeys } from './query-key';
import {
  useLocationInvites,
  useLocationMembers,
  type LocationMembers,
} from '../locations/use-location-members';

export { initializeDefaultOrgID } from './default-org-id';
export { createDeleteOrganizationMutation } from './delete-organization';
export { createDeleteOrganizationMemberMutation } from './delete-organization-member';
export {
  getNextOrgID,
  getNextOrgIDAfterMemberRemoval,
  type Organizations,
} from './get-current-org';
export { useOrgsWithAccessToLocation } from './get-orgs-for-location';
export { prefetchListOrganizations } from './list-organizations';
export { createListOrganizationsByUserQuery } from './list-organizations-by-user';
export { createSearchOrganizationsByCrmIdQuery } from './search-organizations-by-crm-id';
export { createGetOrganizationQuery } from './create-get-organization-query';
export {
  formatLoginMethod,
  LOGIN_METHOD_OPTIONS,
  type LoginMethodOption,
} from './login-methods';
export {
  createGetLogoQuery,
  createGetSupportEmailQuery,
  createSetLogoMutation,
  createSetSupportEmailMutation,
} from './create-white-labeling-queries';
export {
  buildBulkInviteErrorMessages,
  formatInviteFailureMessage,
} from './format-invite-failure';
export { findOrgMemberByEmail } from './members';

const OrgsContextKey = Symbol('orgs');

/** A function to set the current organization by ID. */
export type SetCurrentOrg = (id: string) => unknown;

/** Organizations state and actions. */
export interface OrgsContext {
  organizations: Readable<Organizations>;
  currentOrg: Readable<APITypes.Organization | undefined>;
  currentOrgID: Readable<string | undefined>;
  setCurrentOrg: SetCurrentOrg;
}

/** Provide organizations state and actions to the component tree. */
export const provideOrgsContext = (initialOrgID: string | undefined): void => {
  const selectedOrgID = writable<string | undefined>(initialOrgID);
  const organizationsQuery = createListOrganizationsQuery();
  const { user } = useUserContext();

  const setUserOrgAccessTimestamp = useCreateSetUserOrgLastAccessMutation();
  const setOrgTimestamp = useStoreValue(setUserOrgAccessTimestamp);

  const organizations = derived(
    [selectedOrgID, organizationsQuery, user],
    getCurrentOrg
  );
  const currentOrg = derived(organizations, ({ current }) => current);
  const currentOrgID = derived([organizations], getCurrentOrgID);
  selectedOrgID.subscribe(($id) => {
    if ($id) {
      setOrgTimestamp.current.mutate({ orgId: $id });
    }
  });
  const setCurrentOrg: SetCurrentOrg = (id) => {
    setBrowsersSessionOrgID(id);
    selectedOrgID.set(id);
  };

  setContext<OrgsContext>(OrgsContextKey, {
    organizations,
    currentOrg,
    currentOrgID,
    setCurrentOrg,
  });
};

/**
 * Use organizations state and actions.
 *
 * @returns org state and actions.
 */
export const useOrgsContext = (): OrgsContext => {
  return getContext<OrgsContext>(OrgsContextKey);
};

/** Org state when a parent provided it. Model forms and tests may omit it. */
export const useOptionalOrgsContext = (): OrgsContext | undefined => {
  return getContext<OrgsContext | undefined>(OrgsContextKey);
};

/** A create organization mutator function */
export type CreateOrg = (
  request: APITypes.CreateOrganizationRequest,
  options: {
    onSuccess: (data: APITypes.CreateOrganizationResponse) => unknown;
    onError?: (error: Error) => unknown;
  }
) => unknown;

/** Create an organization */
export const useCreateOrg = (): Readable<CreateOrg> => {
  const mutation = createOrganizationMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

/** An update org mutator function */
export type UpdateOrg = CreateMutateFunction<
  APITypes.UpdateOrganizationResponse,
  Error,
  APITypes.UpdateOrganizationRequest
>;

export const useUpdateOrg = (): Readable<UpdateOrg> => {
  const mutation = createUpdateOrganizationMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

export const useOrganization = (
  orgID: Readable<string | undefined>
): Readable<APITypes.Organization | undefined> => {
  const { organizations } = useOrgsContext();
  return derived([orgID, organizations], ([$orgID, $organizations]) =>
    $orgID ? $organizations.byID[$orgID] : undefined
  );
};

const createOrganizationMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (request: APITypes.CreateOrganizationRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.createOrganization(
        request,
        rpcOptions
      );

      return response;
    },
    onSuccess: async () => {
      await invalidateQueries(getOrgQueryKeys());
    },
  });
};

const createUpdateOrganizationMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (request: APITypes.UpdateOrganizationRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.updateOrganization(
        request,
        rpcOptions
      );

      return response;
    },
    onSuccess: async (_, request) => {
      await invalidateQueries([
        ...getOrgQueryKeys(),
        orgKeys.get(request.organizationId),
      ]);
    },
  });
};

export interface RenameOrgNamespaceMutationOptions {
  onSuccess?: (
    response: APITypes.UpdateOrganizationNamespaceResponse,
    request: APITypes.UpdateOrganizationNamespaceRequest
  ) => unknown;
  onError?: (error: Error) => unknown;
}

export const createRenameOrganizationNamespaceMutation = (
  options: RenameOrgNamespaceMutationOptions
) => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (
      request: APITypes.UpdateOrganizationNamespaceRequest
    ) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.updateOrganizationNamespace(
        request,
        rpcOptions
      );

      return response;
    },
    onSuccess: async (response, request) => {
      await invalidateQueries(getOrgQueryKeys());
      options.onSuccess?.(response, request);
    },
    onError: options.onError,
  });
};

/** Data from an RPC API call to ListOrganizationMembers. */
export interface ListOrganizationMembersQuery {
  data: APITypes.ListOrganizationMembersResponse | undefined;
}

/** Type to separate the current app user member from all other org members */
export interface OrganizationMembers {
  current?: APITypes.OrganizationMember;
  others: APITypes.OrganizationMember[];
}

/**
 * List the invites in an organization.
 *
 * @param organizationID the organization ID
 * @returns a list of organization invites
 */
export const useOrganizationInvites = (
  organizationID: Readable<string | undefined>
): Readable<APITypes.OrganizationInvite[] | undefined> => {
  const listOrgMembersQuery =
    createListOrgMembersAndInvitesQuery(organizationID);

  return derived(listOrgMembersQuery, ($query) => {
    const invites = $query.data?.invites;
    return invites ? [...invites].sort(sortInvitesByRoleThenEmail) : undefined;
  });
};

/**
 * List the members of an organization.
 *
 * @param organizationID the organization ID
 * @returns a list of organization members
 */
export const useOrganizationMembers = (
  organizationID: Readable<string | undefined>,
  mustIncludeCurrentUser = true
): Readable<OrganizationMembers | undefined> => {
  const listOrgMembersQuery =
    createListOrgMembersAndInvitesQuery(organizationID);
  const authsByUserIDMap = useAuthsByIdentityIDMap(organizationID);
  const { userID } = useUserContext();

  return derived(
    [listOrgMembersQuery, authsByUserIDMap, userID],
    ([$query, $authsByUserID, $userID]) =>
      buildOrganizationMembers(
        $query,
        $authsByUserID,
        $userID,
        mustIncludeCurrentUser
      )
  );
};

const createListOrgMembersAndInvitesQuery = (
  organizationID: Readable<string | undefined>
) => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useAPIClient();

  const queryOptions = derived(
    [authState, organizationID],
    ([$authState, $organizationID]) =>
      createQueryOptions({
        queryKey: orgKeys.membersAndInvites($organizationID),
        enabled:
          $authState.status === AuthStatus.LoggedIn &&
          $organizationID !== undefined,
        queryFn: async () => {
          assertExists($organizationID, 'Missing org ID');

          const rpcOptions = await getRPCOptions();
          const { response } = await apiClient.listOrganizationMembers(
            {
              organizationId: $organizationID,
            },
            rpcOptions
          );

          return response;
        },
      })
  );

  return createQuery(queryOptions);
};

/** A delete organization invite mutator function */
export type DeleteOrganizationInvite = CreateMutateFunction<
  APITypes.DeleteOrganizationInviteResponse,
  Error,
  APITypes.DeleteOrganizationInviteRequest
>;

/** Used to delete an invite from an organization */
export const useDeleteOrganizationInvite =
  (): Readable<DeleteOrganizationInvite> => {
    const mutation = createDeleteOrgInviteMutation();

    return derived([mutation], ([$mutation]) => $mutation.mutate);
  };

const createDeleteOrgInviteMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (request: APITypes.DeleteOrganizationInviteRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.deleteOrganizationInvite(
        request,
        rpcOptions
      );

      return response;
    },
    onSuccess: async (_data, request) => {
      await invalidateQueries([
        orgKeys.membersAndInvites(request.organizationId),
      ]);
    },
  });
};

/** A create organization invite mutator function */
export type CreateOrganizationInvite = CreateMutateFunction<
  APITypes.CreateOrganizationInviteResponse,
  Error,
  APITypes.CreateOrganizationInviteRequest
>;

/** Used to create an invite for an organization */
export const useCreateOrganizationInvite =
  (): Readable<CreateOrganizationInvite> => {
    const mutation = createCreateOrgInviteMutation();

    return derived([mutation], ([$mutation]) => $mutation.mutate);
  };

export interface CreateOrgInviteParams
  extends APITypes.CreateOrganizationInviteRequest {
  locationID?: string;
}

const createCreateOrgInviteMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (params: CreateOrgInviteParams) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.createOrganizationInvite(
        {
          organizationId: params.organizationId,
          email: params.email,
          authorizations: params.authorizations,
          sendEmailInvite: params.sendEmailInvite,
        },
        rpcOptions
      );

      return response;
    },
    onSuccess: async (_data, params) => {
      await invalidateQueries([
        orgKeys.membersAndInvites(params.organizationId),
        locationKeys.membersAndInvites(params.locationID),
      ]);
    },
  });
};

interface ValidationParams {
  request: APITypes.CreateOrganizationInviteRequest;
  orgMembers?: OrganizationMembers | undefined;
  orgInvites?: APITypes.OrganizationInvite[] | undefined;
  locMembers?: LocationMembers | undefined;
  locInvites?: APITypes.OrganizationInvite[] | undefined;
}

// Must stay a substring of the CreateOrganizationInvite rejection for existing
// members in domains/service/server.go — the server comment points back here.
const USER_ALREADY_IN_ORG_MESSAGE = 'already in the organization';

const validateInviteRequest = ({
  request,
  orgMembers,
  locMembers,
  locInvites,
}: ValidationParams) => {
  if (!request.email) {
    return 'Must provide email address';
  }

  if (
    locMembers?.current.emails.includes(request.email) ||
    locMembers?.others.some(({ emails }) => emails.includes(request.email))
  ) {
    return `${request.email} is already in the location`;
  }

  if (locInvites?.some(({ email }) => email === request.email)) {
    return `${request.email} is already invited to the location`;
  }

  if (findOrgMemberByEmail(orgMembers, request.email)) {
    return `${request.email} is already in the organization`;
  }

  if (request.authorizations.length === 0) {
    return 'No resource selected';
  }

  return null;
};

/** Options for creating an org invite with notifications and callbacks */
export interface CreateOrgInviteNotifyOptions extends QueryStateOptions {
  /** Called after the invite (or invite update) succeeds, before the success toast */
  onSuccess?: () => void;
  /**
   * Called instead of the error toast when the invited email already belongs
   * to an org member — detected client-side when the member list is loaded,
   * or from the server's rejection when it isn't.
   */
  onUserAlreadyInOrganization?: () => void;
}

/** Function signature for a GRPC call to create organization invite, with success/error options */
export type CreateOrganizationInviteWithNotify = (
  params: CreateOrgInviteParams,
  options?: CreateOrgInviteNotifyOptions
) => void;

/** A store holding a create org invite mutation, with success/error options */
export const useCreateOrganizationInviteWithNotify = (
  organizationId: Readable<string | undefined>,
  locationId?: string
): Readable<CreateOrganizationInviteWithNotify> => {
  const notify = useNotify();
  const baseCreateOrganizationInvite = useCreateOrganizationInvite();
  const baseUpdateOrgInviteAuths = useUpdateOrgInviteAuths();
  const orgMembers = useOrganizationMembers(organizationId);
  const orgInvites = useOrganizationInvites(organizationId);
  const locMembers = useLocationMembers(locationId);
  const locInvites = useLocationInvites(locationId);

  return derived(
    [
      baseCreateOrganizationInvite,
      baseUpdateOrgInviteAuths,
      orgMembers,
      orgInvites,
      locMembers,
      locInvites,
    ],
    ([
      $createOrganizationInvite,
      $updateOrgInviteAuths,
      $orgMembers,
      $orgInvites,
      $locMembers,
      $locInvites,
    ]) =>
      (
        params: CreateOrgInviteParams,
        options?: CreateOrgInviteNotifyOptions
      ) => {
        const request = {
          organizationId: params.organizationId,
          email: params.email,
          authorizations: params.authorizations,
          sendEmailInvite: params.sendEmailInvite,
        };

        if (
          options?.onUserAlreadyInOrganization &&
          findOrgMemberByEmail($orgMembers, request.email)
        ) {
          options.onUserAlreadyInOrganization();
          return;
        }

        const validationError = validateInviteRequest({
          request,
          orgMembers: $orgMembers,
          locMembers: $locMembers,
          locInvites: $locInvites,
        });

        if (validationError) {
          notify.danger(validationError);
          return;
        }

        const onSuccess = () => {
          options?.onSuccess?.();
          if (options?.successMessage) {
            notify.success(options.successMessage);
          }
        };
        const onError = (error: Error) => {
          if (
            options?.onUserAlreadyInOrganization &&
            error.message.includes(USER_ALREADY_IN_ORG_MESSAGE)
          ) {
            options.onUserAlreadyInOrganization();
            return;
          }
          notify.danger(
            `${options?.errorMessage ?? 'Error'}: ${error.message}`
          );
        };

        if ($orgInvites?.some(({ email }) => email === request.email)) {
          $updateOrgInviteAuths(
            {
              organizationId: params.organizationId,
              email: params.email,
              addAuthorizations: params.authorizations,
              removeAuthorizations: [],
              locationID: params.locationID,
            },
            {
              onSuccess,
              onError,
            }
          );
        } else {
          $createOrganizationInvite(params, {
            onSuccess,
            onError,
          });
        }
      }
  );
};

export interface BulkCreateOrgInvitesParams {
  organizationId: string;
  emails: string[];
  authorizations: APITypes.Authorization[];
  locationID?: string;
  sendEmailInvite?: boolean;
}

export interface BulkInviteResult {
  succeeded: string[];
  failed: { email: string; error: string }[];
  skipped: { email: string; reason: string }[];
}

export type BulkCreateOrganizationInvitesWithNotify = (
  params: BulkCreateOrgInvitesParams,
  options?: {
    onProgress?: (completed: number, total: number) => void;
  }
) => Promise<BulkInviteResult>;

const notifyBulkInviteSuccess = (
  notify: ReturnType<typeof useNotify>,
  result: BulkInviteResult,
  total: number
): void => {
  const successCount = result.succeeded.length;
  if (successCount === 0) {
    return;
  }

  const failCount = result.failed.length;
  const skipCount = result.skipped.length;

  if (failCount === 0 && skipCount === 0) {
    const invitedEmail = result.succeeded[0] ?? '';
    notify.success(
      successCount === 1
        ? `Invited ${invitedEmail}`
        : `Invited ${successCount} users`
    );
    return;
  }

  notify.success(`Invited ${successCount} of ${total} users`);
};

/** Bulk-create org invites by looping the single-invite APIs, with one summary toast. */
export const useBulkCreateOrganizationInvitesWithNotify = (
  organizationId: Readable<string | undefined>,
  locationId?: string
): Readable<BulkCreateOrganizationInvitesWithNotify> => {
  const notify = useNotify();
  const createInviteMutation = createCreateOrgInviteMutation();
  const updateInviteAuthsMutation = createUpdateOrgInviteAuthsMutation();
  const orgMembers = useOrganizationMembers(organizationId);
  const orgInvites = useOrganizationInvites(organizationId);
  const locMembers = useLocationMembers(locationId);
  const locInvites = useLocationInvites(locationId);

  const bulkCreate: BulkCreateOrganizationInvitesWithNotify = async (
    params,
    options
  ) => {
    const result: BulkInviteResult = {
      succeeded: [],
      failed: [],
      skipped: [],
    };
    const total = params.emails.length;
    let completed = 0;

    for (const email of params.emails) {
      const request = {
        organizationId: params.organizationId,
        email,
        authorizations: params.authorizations,
        sendEmailInvite: params.sendEmailInvite,
      };
      const validationError = validateInviteRequest({
        request,
        orgMembers: get(orgMembers),
        locMembers: get(locMembers),
        locInvites: get(locInvites),
      });

      if (validationError) {
        result.skipped.push({ email, reason: validationError });
        completed += 1;
        options?.onProgress?.(completed, total);
        continue;
      }

      try {
        // Sequential awaits keep invite rate and progress reporting predictable.
        /* eslint-disable no-await-in-loop -- intentional sequential invite sends */
        const hasExistingInvite = get(orgInvites)?.some(
          (invite) => invite.email === email
        );
        await (hasExistingInvite
          ? get(updateInviteAuthsMutation).mutateAsync({
              organizationId: params.organizationId,
              email,
              addAuthorizations: params.authorizations,
              removeAuthorizations: [],
              locationID: params.locationID,
            })
          : get(createInviteMutation).mutateAsync({
              organizationId: params.organizationId,
              email,
              authorizations: params.authorizations,
              sendEmailInvite: params.sendEmailInvite,
              locationID: params.locationID,
            }));
        /* eslint-enable no-await-in-loop */
        result.succeeded.push(email);
      } catch (error) {
        result.failed.push({
          email,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      completed += 1;
      options?.onProgress?.(completed, total);
    }

    notifyBulkInviteSuccess(notify, result, total);
    return result;
  };

  return readable(bulkCreate);
};

interface UpdateOrgInviteAuthsParams
  extends APITypes.UpdateOrganizationInviteAuthorizationsRequest {
  locationID?: string;
}

/** An update organization invite mutator function */
type UpdateOrgInviteAuths = CreateMutateFunction<
  APITypes.UpdateOrganizationInviteAuthorizationsResponse,
  Error,
  UpdateOrgInviteAuthsParams
>;

/** Used to update an invite for an organization */
const useUpdateOrgInviteAuths = (): Readable<UpdateOrgInviteAuths> => {
  const mutation = createUpdateOrgInviteAuthsMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

const createUpdateOrgInviteAuthsMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (params: UpdateOrgInviteAuthsParams) => {
      const rpcOptions = await getRPCOptions();
      const { response } =
        await apiClient.updateOrganizationInviteAuthorizations(
          {
            organizationId: params.organizationId,
            email: params.email,
            addAuthorizations: params.addAuthorizations,
            removeAuthorizations: params.removeAuthorizations,
          },
          rpcOptions
        );

      return response;
    },
    onSuccess: async (_data, params) => {
      await invalidateQueries([
        orgKeys.membersAndInvites(params.organizationId),
        locationKeys.membersAndInvites(params.locationID),
      ]);
    },
  });
};

/** A resend organization invite mutator function */
export type ResendOrganizationInvite = CreateMutateFunction<
  APITypes.ResendOrganizationInviteResponse,
  Error,
  APITypes.ResendOrganizationInviteRequest
>;

/** Used to resend an invite */
export const useResendOrganizationInvite =
  (): Readable<ResendOrganizationInvite> => {
    const mutation = createResendOrgInviteMutation();

    return derived([mutation], ([$mutation]) => $mutation.mutate);
  };

const createResendOrgInviteMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();

  return createMutation({
    mutationFn: async (request: APITypes.ResendOrganizationInviteRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.resendOrganizationInvite(
        request,
        rpcOptions
      );

      return response;
    },
  });
};

export const createGetOrgNamespaceAvailabilityQuery = (
  orgID: string,
  publicNamespace: Readable<string>
) => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useAPIClient();

  const queryOptions = derived(
    [authState, publicNamespace],
    ([$authState, $publicNamespace]) => ({
      queryKey: ['namespaceAvailability', $publicNamespace],
      enabled:
        $authState.status === AuthStatus.LoggedIn && Boolean($publicNamespace),
      queryFn: async () => {
        const rpcOptions = await getRPCOptions();
        const { response } =
          await apiClient.getOrganizationNamespaceAvailability(
            {
              organizationId: orgID,
              publicNamespace: $publicNamespace,
            },
            rpcOptions
          );

        return response;
      },
    })
  );

  return createQuery(queryOptions);
};
